import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@shared/components/ui/card";
import { cn } from "@shared/utils/cn";
import type { AvailableArea } from "@features/booking/types/timeSlots";
import type { PublicArea } from "@shared/types/restaurant";
import { localizeAreaById } from "@features/booking/utils/areaI18n";

interface AreaListProps {
  /** Slot-level areas — canonical values only, no `translations` envelope. */
  areas: AvailableArea[];
  onSelect: (areaId: number) => void;
  selectedArea: string | null;
  /**
   * Restaurant-level `publicAreas` — the only area payload carrying
   * `translations`. Optional: absent (older cached payloads) renders canonical.
   */
  publicAreas?: readonly PublicArea[];
}

const AreaList = ({ areas, onSelect, selectedArea, publicAreas }: AreaListProps) => {
  const { t, i18n } = useTranslation('booking');

  return (
    <div className="w-full space-y-4">
      {areas.map((area) => {
        const isSelected = selectedArea === String(area.id);
        const disabled = !area.available;
        // Display only. `area` itself is never mutated or replaced — onSelect
        // reports the id, and the caller stores the CANONICAL name because
        // areaUtils.menuMatchesArea's scalar fallback compares against it.
        const display = localizeAreaById(area, publicAreas, i18n.language);
        const handleActivate = () => {
          if (!disabled) onSelect(area.id);
        };
        return (
          <Card
            key={area.id}
            className={cn(
              "overflow-hidden border-2 transition-colors",
              isSelected ? "border-brand" : "border-border",
              disabled
                ? "opacity-60 cursor-not-allowed"
                : "cursor-pointer hover:border-brand-light",
            )}
            role="button"
            tabIndex={0}
            aria-disabled={disabled}
            aria-pressed={isSelected}
            onClick={handleActivate}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleActivate();
              }
            }}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-xl font-medium text-foreground mb-1">
                    {display.name}
                  </h3>
                  {display.description && (
                    <p className="text-foreground/60 text-sm">
                      {display.description}
                    </p>
                  )}
                  {disabled && (
                    <p className="text-foreground/50 text-sm font-medium mt-2">
                      {t('NOT_AVAILABLE')}
                    </p>
                  )}
                </div>
                {!disabled && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-foreground/60 shrink-0 mt-1"
                    width={20}
                    height={20}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M5 12h14M13 6l6 6-6 6" strokeWidth={2} />
                  </svg>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default AreaList;
